<template>
	<div class="outside-hospital-report-list">
		<div v-if="loading" class="outside-hospital-report-list__state">
			<van-loading color="#5f5aa3" size="24px">加载中...</van-loading>
		</div>

		<template v-else-if="reports.length">
			<div class="outside-hospital-report-list__summary">
				<span>外院报告</span>
				<span>共 {{ reportCount }} 份，{{ imageCount }} 张</span>
			</div>
			<div class="outside-hospital-report-list__grid">
				<article v-for="(report, reportIndex) in reports" :key="report.id || reportIndex" class="outside-hospital-report-list__item">
					<div class="outside-hospital-report-list__images">
						<button v-for="(image, imageIndex) in report.images" :key="image.id || imageIndex" type="button" class="outside-hospital-report-list__image-button" @click="preview(report, imageIndex)">
							<van-image :src="image.previewUrl" fit="cover" class="outside-hospital-report-list__image">
								<template #loading><van-loading type="spinner" size="20" /></template>
								<template #error><van-icon name="photo-fail" /></template>
							</van-image>
							<span class="outside-hospital-report-list__zoom"><van-icon name="expand-o" /></span>
						</button>
					</div>
					<div class="outside-hospital-report-list__meta">
						<div class="outside-hospital-report-list__meta-title">{{ report.inspectionHospital || '未填写检查医院' }}</div>
						<div class="outside-hospital-report-list__meta-description">检查时间：{{ formatInspectionTime(report.inspectionTime) }}</div>
						<div v-if="report.images.some(image => image.failed)" class="outside-hospital-report-list__meta-error">部分图片加载失败，点击图片重试</div>
					</div>
				</article>
			</div>
		</template>

		<div v-else-if="loadFailed" class="outside-hospital-report-list__state">
			<van-empty image="network" description="外院报告加载失败">
				<van-button size="small" round color="#5f5aa3" @click="loadReports">重新加载</van-button>
			</van-empty>
		</div>

		<van-empty v-else description="暂无外院报告" />
	</div>
</template>

<script>
import api from "@/request/api";
import { ImagePreview } from "vant";
import dayjs from "dayjs";

export default {
	name: "OutsideHospitalReportList",
	props: {
		jzlsh: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			loading: false,
			loadFailed: false,
			reports: [],
			loadVersion: 0,
		};
	},
	computed: {
		reportCount() {
			return this.reports.length;
		},
		imageCount() {
			return this.reports.reduce((count, report) => count + report.images.length, 0);
		},
	},
	watch: {
		jzlsh: {
			immediate: true,
			handler() {
				this.loadReports();
			},
		},
	},
	beforeDestroy() {
		this.loadVersion += 1;
		this.releaseImages();
	},
	methods: {
		releaseImages() {
			if (this.imagePreview) {
				this.imagePreview.close();
				this.imagePreview = null;
			}
			this.reports.forEach((report) => {
				report.images.forEach((image) => {
					if (image.previewUrl) URL.revokeObjectURL(image.previewUrl);
				});
			});
		},
		async loadImage(item, version) {
			if (item.loading) return;
			item.loading = true;
			item.failed = false;
			try {
				if (!item.id) throw new Error('缺少报告 ID');
				const blob = await api.outsideHospitalReportImage(item.id);
				if (!(blob instanceof Blob) || !blob.size || /json|text\/html/i.test(blob.type)) {
					throw new Error('图片响应无效');
				}
				if (version !== this.loadVersion) return;
				item.previewUrl = URL.createObjectURL(blob);
			} catch (error) {
				if (version === this.loadVersion) item.failed = true;
			} finally {
				item.loading = false;
			}
		},
		async loadReports() {
			const version = ++this.loadVersion;
			this.releaseImages();
			this.reports = [];
			this.loadFailed = false;
			this.loading = false;
			if (!this.jzlsh) return;
			this.loading = true;
			try {
				const res = await api.outsideHospitalReportList(this.jzlsh);
				if (version !== this.loadVersion) return;
				if (!res || !res.success) throw new Error((res && res.message) || "加载失败");
				const result = Array.isArray(res.result) ? res.result : (res.result && res.result.records) || [];
				this.reports = result.map((mainReport) => {
					const reportList = Array.isArray(mainReport.reportList) ? mainReport.reportList : [];
					return {
						...mainReport,
						images: reportList.map((report) => ({
							...report,
							previewUrl: "",
							loading: false,
							failed: false,
						})),
					};
				}).filter(report => report.images.length);

				const images = this.reports.reduce((list, report) => list.concat(report.images), []);
				await Promise.all(images.map((image) => this.loadImage(image, version)));
			} catch (error) {
				if (version === this.loadVersion) this.loadFailed = true;
			} finally {
				if (version === this.loadVersion) this.loading = false;
			}
		},
		preview(report, imageIndex) {
			const image = report.images[imageIndex];
			if (!image.previewUrl) {
				this.loadImage(image, this.loadVersion);
				return;
			}
			const images = report.images.filter((item) => item.previewUrl);
			this.imagePreview = ImagePreview({
				images: images.map((item) => item.previewUrl),
				startPosition: images.indexOf(image),
				closeable: true,
				showIndex: true,
				minZoom: 0.5,
				maxZoom: 5,
			});
		},
		formatInspectionTime(value) {
			if (!value) return "未填写";
			const date = dayjs(value);
			return date.isValid() ? date.format("YYYY-MM-DD") : value;
		},
	},
};
</script>

<style lang="less" scoped>
.outside-hospital-report-list {
	min-height: 10rem;
	padding: 0 0.533rem 1.6rem;
	box-sizing: border-box;

	&__summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.4rem 0.107rem;
		font-size: 0.64rem;
	}

	&__grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: 0.4rem;
	}

	&__item {
		min-width: 0;
		padding: 0;
		overflow: hidden;
		text-align: left;
		background: #fff;
		border: 0;
		border-radius: 0.32rem;
		box-shadow: 0 0.107rem 0.32rem rgba(40, 38, 72, 0.08);
		transition: box-shadow 0.2s ease;

		&:active {
			box-shadow: 0 0.053rem 0.16rem rgba(40, 38, 72, 0.12);
		}
	}

	&__images {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 0.16rem;
		padding: 0.16rem 0.16rem 0;
	}

	&__image-button {
		position: relative;
		display: block;
		width: 100%;
		aspect-ratio: 1;
		padding: 0;
		overflow: hidden;
		background: #eeeef2;
		border: 0;
		border-radius: 0.213rem;
	}

	&__image {
		display: block;
		width: 100%;
		height: 100%;
		background: #eeeef2;

		/deep/ .van-image__img {
			height: 100%;
		}
	}

	&__zoom {
		position: absolute;
		top: 0.16rem;
		right: 0.16rem;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 0.747rem;
		height: 0.747rem;
		font-size: 0.427rem;
		color: #fff;
		background: rgba(0, 0, 0, 0.48);
		border-radius: 50%;
	}

	&__meta {
		display: block;
		padding: 0.373rem 0.427rem 0.4rem;
	}

	&__meta-title {
		display: block;
		overflow: hidden;
		font-size: 0.693rem;
		font-weight: 500;
		line-height: 0.96rem;
		color: #303033;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__meta-description,
	&__meta-error {
		display: block;
		margin-top: 0.133rem;
		font-size: 0.587rem;
		line-height: 0.8rem;
		color: #969799;
	}

	&__meta-error {
		color: #ee0a24;
	}

	&__state {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 8rem;
	}
}
</style>
